import React, { useEffect, useState } from "react";
import api from "../../../api";
import ProductMainRow from "./ProductMainRow";
import WarehousePanel from "./WarehousePanel";
import Pagination from "../../Pagination";
import Button from "../../Button";

import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { FiX } from "react-icons/fi";

// value ها دقیقاً کلیدهای مجاز در allowed_ordering بک‌اند هستند
const SORT_OPTIONS = [
  { label: "جدیدترین", value: "newest" },
  { label: "قدیمی‌ترین", value: "oldest" },
  { label: "جدیدترین بازبینی", value: "newest_review" },
  { label: "قدیمی‌ترین بازبینی", value: "oldest_review" },
];

const ACTIVE_OPTIONS = [
  { value: "", label: "همه کالاها" },
  { value: "true", label: "کالاهای فعال" },
  { value: "false", label: "کالاهای غیرفعال" },
];

const DEFAULT_FILTERS = {
  search: "",
  isActive: "",
  dateFrom: null,
  dateTo: null,
  ordering: "newest",
};

const ProductBasedInventory = () => {
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);

  // state فرم فیلترها
  const [search, setSearch] = useState(DEFAULT_FILTERS.search);
  const [isActive, setIsActive] = useState(DEFAULT_FILTERS.isActive);
  const [dateFrom, setDateFrom] = useState(DEFAULT_FILTERS.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_FILTERS.dateTo);
  const [ordering, setOrdering] = useState(DEFAULT_FILTERS.ordering);

  // state فیلترهای اعمال‌شده
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const toGregorianISO = (date) => {
    if (!date) return "";
    try {
      return date.toDate().toISOString();
    } catch {
      return "";
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};

      if (appliedFilters.search?.trim()) {
        params.search = appliedFilters.search.trim();
      }

      if (appliedFilters.isActive !== "") {
        params.is_active = appliedFilters.isActive;
      }

      if (appliedFilters.dateFrom) {
        params.date_from = toGregorianISO(appliedFilters.dateFrom);
      }

      if (appliedFilters.dateTo) {
        params.date_to = toGregorianISO(appliedFilters.dateTo);
      }

      if (appliedFilters.ordering?.trim()) {
        params.ordering = appliedFilters.ordering;
      }

      params.page = page;
      params.page_size = pageSize;

      const { data } = await api.get("/products/inventory/product_table/", {
        params,
      });

      setProducts(data.results || []);
      setTotalCount(data.count || 0);
      setExpandedRows({});
    } catch (err) {
      console.error(err);
      alert("خطا در دریافت اطلاعات کالاها");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (productId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [productId]: !prev[productId],
    }));
  };

  // مرتب‌سازی بلافاصله اعمال می‌شود (بدون نیاز به زدن جستجو)
  const handleOrderingChange = (value) => {
    setOrdering(value);
    setPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      ordering: value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const nextFilters = {
      search,
      isActive,
      dateFrom,
      dateTo,
      ordering,
    };

    setPage(1);
    setAppliedFilters(nextFilters);
  };

  const handleClear = () => {
    setSearch(DEFAULT_FILTERS.search);
    setIsActive(DEFAULT_FILTERS.isActive);
    setDateFrom(DEFAULT_FILTERS.dateFrom);
    setDateTo(DEFAULT_FILTERS.dateTo);
    setOrdering(DEFAULT_FILTERS.ordering);

    setPage(1);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedFilters]);

  const hasFilters = !!(
    search ||
    isActive ||
    dateFrom ||
    dateTo ||
    ordering !== DEFAULT_FILTERS.ordering
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) {
    return (
      <div className="py-8 text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-blue-500"></div>
        <p className="mt-2 text-gray-600">در حال بارگذاری...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">مدیریت موجودی بر اساس کالا</h2>
          <p className="text-sm text-gray-500">تعداد کالا: {totalCount}</p>
        </div>

        <form onSubmit={handleSearch} className="space-y-3">
          <div
            className="
              grid grid-cols-1 gap-3
              sm:grid-cols-2
              xl:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto]
              items-center
            "
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="جستجو بر اساس نام، کد، برند، دسته..."
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />

            <select
              value={isActive}
              onChange={(e) => setIsActive(e.target.value)}
              className="rounded-lg border border-gray-300 p-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]"
            >
              {ACTIVE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <DatePicker
              value={dateFrom}
              onChange={setDateFrom}
              calendar={persian}
              locale={persian_fa}
              placeholder="از تاریخ"
              inputClass="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />

            <DatePicker
              value={dateTo}
              onChange={setDateTo}
              calendar={persian}
              locale={persian_fa}
              placeholder="تا تاریخ"
              inputClass="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            />

            <div className="flex items-center gap-2">
              <Button type="submit" className="w-full sm:w-auto">
                جستجو
              </Button>

              {hasFilters && (
                <button
                  type="button"
                  onClick={handleClear}
                  title="پاک کردن فیلترها"
                  className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-all hover:border-red-400 hover:bg-red-50 hover:text-red-600"
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
          </div>
        </form>

        <div className="mb-[16px] flex items-center gap-2 border-b pb-4">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleOrderingChange(option.value)}
              className={`px-3 py-1 text-sm rounded-md border transition ${
                ordering === option.value
                  ? "bg-[#F5F5F5] text-[#1E3A5F] font-semibold border-[#1E3A5F]"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <table className="min-w-full table-fixed">
        <thead className="bg-gray-100">
          <tr>
            <th className="w-10 px-3 py-3 text-right"></th>
            <th className="w-20 px-3 py-3 text-right">عکس</th>
            <th className="w-52 px-3 py-3 text-right">نام کالا</th>
            <th className="w-40 px-3 py-3 text-right">دسته</th>
            <th className="w-32 px-3 py-3 text-right">برند</th>
            <th className="w-24 px-3 py-3 text-right">واحد اندازه گیری</th>
          </tr>
        </thead>

        <tbody>
          {products.map((product) => (
            <React.Fragment key={product.id}>
              <ProductMainRow
                product={product}
                expanded={expandedRows[product.id]}
                onToggleExpand={() => toggleExpand(product.id)}
              />

              {expandedRows[product.id] && <WarehousePanel product={product} />}
            </React.Fragment>
          ))}

          {products.length === 0 && !loading && (
            <tr>
              <td colSpan={6} className="py-8 text-center text-gray-500">
                {appliedFilters.search
                  ? "کالایی با این مشخصات یافت نشد"
                  : "کالایی وجود ندارد"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Pagination page={page} setPage={setPage} totalPages={totalPages} />
    </div>
  );
};

export default ProductBasedInventory;
